import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Copy, Check, RefreshCw, ExternalLink, Clock, Shield, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { generateFastLoginToken, revokeFastLoginToken } from "../../services/crmServices/userService";

export function FastLoginModal({ open, onClose, staff }) {
    const [fastLoginToken, setFastLoginToken] = useState("");
    const [fastLoginUrl, setFastLoginUrl] = useState("");
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [tokenExpiry, setTokenExpiry] = useState(null);

    useEffect(() => {
        if (open && staff) {
            generateToken();
        }
    }, [open, staff]);

    const generateToken = async () => {
        if (!staff?._id) return;

        setLoading(true);
        try {
            const response = await generateFastLoginToken(staff._id);
            if (response.data?.token) {
                const token = response.data.token;
                const expiry = response.data.expiresAt;

                setFastLoginToken(token);
                setTokenExpiry(expiry);

                // Generate the fast login URL
                const baseUrl = window.location.origin;
                const loginUrl = `${baseUrl}/fast-login?token=${token}`;
                setFastLoginUrl(loginUrl);

                toast.success("Fast login token generated successfully!");
            } else {
                toast.error(response?.data?.message || "Failed to generate fast login token");
            }
        } catch (error) {
            console.error("Error generating fast login token:", error);
            toast.error("Failed to generate fast login token");
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(fastLoginUrl);
            setCopied(true);
            toast.success("Fast login URL copied to clipboard!");

            // Reset copied state after 2 seconds
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error("Failed to copy to clipboard:", error);
            toast.error("Failed to copy to clipboard");
        }
    };

    const openInNewTab = () => {
        if (fastLoginUrl) {
            window.open(fastLoginUrl, "_blank");
        }
    };

    const revokeToken = async () => {
        if (!staff?._id) return;

        setLoading(true);
        try {
            const response = await revokeFastLoginToken(staff._id);
            if (response?.status === 200) {
                setFastLoginToken("");
                setFastLoginUrl("");
                setTokenExpiry(null);
                toast.success("Fast login token revoked successfully!");
            } else {
                toast.error(response?.data?.message || "Failed to revoke fast login token");
            }
        } catch (error) {
            console.error("Error revoking fast login token:", error);
            toast.error("Failed to revoke fast login token");
        } finally {
            setLoading(false);
        }
    };

    const formatExpiry = (expiryDate) => {
        if (!expiryDate) return "Unknown";

        const expiry = new Date(expiryDate);
        const now = new Date();
        const diffMs = expiry.getTime() - now.getTime();
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

        if (diffMs <= 0) {
            return "Expired";
        } else if (diffHours > 0) {
            return `${diffHours}h ${diffMinutes}m remaining`;
        } else {
            return `${diffMinutes}m remaining`;
        }
    };

    if (!staff) return null;

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader className="border-b pb-4">
                    <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center">
                        <Shield className="h-6 w-6 mr-3 text-blue-600" />
                        Fast Login for {staff.firstName} {staff.lastName}
                    </DialogTitle>
                    <DialogDescription className="text-gray-600">
                        Generate a secure login link that allows this user to access the system without entering
                        credentials.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5 pt-2">
                    {/* Staff Information */}
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                            <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            Staff Details
                        </h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-600">Name:</span>
                                <span className="ml-2 font-medium text-gray-900">
                                    {staff.firstName} {staff.lastName}
                                </span>
                            </div>
                            <div>
                                <span className="text-gray-600">Email:</span>
                                <span className="ml-2 text-gray-900">{staff.email}</span>
                            </div>
                            <div>
                                <span className="text-gray-600">Position:</span>
                                <Badge className="ml-2 bg-blue-600 text-white">{staff.position.name}</Badge>
                            </div>
                            <div>
                                <span className="text-gray-600">Fast Login Enabled:</span>
                                <Badge
                                    className={staff.canFastLogin ? "ml-2 bg-green-600 text-white" : "ml-2 bg-gray-400 text-white"}
                                >
                                    {staff.canFastLogin ? "Yes" : "No"}
                                </Badge>
                            </div>
                        </div>
                    </div>

                    {/* Fast Login URL Section */}
                    {fastLoginUrl ? (
                        <div className="space-y-4">
                            <div className="bg-green-50 p-4 rounded-lg border border-green-200 shadow-sm">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="font-semibold text-green-900">Fast Login URL Generated</h4>
                                    {tokenExpiry && (
                                        <div className="flex items-center text-sm text-green-700">
                                            <Clock className="h-4 w-4 mr-1" />
                                            {formatExpiry(tokenExpiry)}
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-3">
                                    <div>
                                        <Label htmlFor="fastLoginUrl" className="text-gray-900 font-semibold">Fast Login URL</Label>
                                        <div className="flex space-x-2">
                                            <Input
                                                id="fastLoginUrl"
                                                value={fastLoginUrl}
                                                readOnly
                                                className="font-mono text-sm"
                                            />
                                            <Button
                                                onClick={copyToClipboard}
                                                variant="outline"
                                                size="icon"
                                                className="shrink-0"
                                            >
                                                {copied ? (
                                                    <Check className="h-4 w-4 text-green-600" />
                                                ) : (
                                                    <Copy className="h-4 w-4" />
                                                )}
                                            </Button>
                                            {/* <Button
                                                onClick={openInNewTab}
                                                variant="outline"
                                                size="icon"
                                                className="shrink-0"
                                            >
                                                <ExternalLink className="h-4 w-4" />
                                            </Button> */}
                                        </div>
                                    </div>

                                    <div className="flex space-x-3">
                                        <Button
                                            onClick={copyToClipboard}
                                            className="flex-1 bg-green-600 hover:bg-green-700:bg-green-600 text-white shadow-md hover:shadow-lg transition-all duration-200"
                                        >
                                            <Copy className="h-4 w-4 mr-2" />
                                            {copied ? "Copied!" : "Copy URL"}
                                        </Button>
                                        <Button
                                            onClick={generateToken}
                                            variant="outline"
                                            disabled={loading}
                                            className="flex-1"
                                        >
                                            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                                            Generate New Token
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                            <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h4 className="font-semibold text-gray-900 mb-2">No Active Fast Login Token</h4>
                            <p className="text-gray-600 mb-4">Generate a secure token to create a fast login link.</p>
                            <Button
                                onClick={generateToken}
                                disabled={loading || !staff.canFastLogin}
                                className="bg-blue-600 hover:bg-blue-700:bg-blue-600 text-white shadow-md hover:shadow-lg transition-all duration-200"
                            >
                                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                                {loading ? "Generating..." : "Generate Fast Login Token"}
                            </Button>
                            {!staff.canFastLogin && (
                                <p className="text-sm text-red-600 mt-2 font-medium">
                                    Fast login is disabled for this user. Enable it in user settings first.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Security Information */}
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 shadow-sm">
                        <h4 className="font-semibold text-blue-900 mb-2">Security Information</h4>
                        <ul className="text-sm text-blue-800 space-y-1">
                            <li>• Fast login tokens expire after 24 hours for security</li>
                            <li>• Each token can only be used once</li>
                            <li>• Tokens are automatically revoked when a new one is generated</li>
                            <li>• The user will be logged in with their normal permissions</li>
                            <li>• All login activity is logged for security auditing</li>
                        </ul>
                    </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <Button variant="outline" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
